import React, { useEffect, useId, useRef } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Info, Loader2 } from "lucide-react";

// `tip` and `secondaryAction` are optional and default to nothing, so callers
// that predate them render exactly as before.
const ConfirmDialog = ({
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
  // A short line under the message: what to do about it, not what happened.
  tip = null,
  // A third, low-emphasis action. Sits left of cancel so it never competes
  // with the primary. { label, href } opens in a new tab; { label, onClick }
  // runs in place.
  secondaryAction = null,
}) => {
  const labelId = useId();
  const descId = useId();
  const cancelRef = useRef(null);

  // Clicking the backdrop already dismissed this; Escape did nothing, which is
  // the one thing every modal is expected to do. Ignored while busy, for the
  // same reason the backdrop is.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // Claimed in the capture phase and marked handled, so a panel open behind
      // this dialog (which also closes on Esc) does not close with it.
      e.preventDefault();
      if (!busy) onCancel?.();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [busy, onCancel]);

  // CANCEL TAKES FOCUS, NEVER CONFIRM. A dialog that opens with the destructive
  // button focused turns a stray Enter — from the keypress that opened it — into
  // the irreversible action. Focusing cancel makes that same keypress the safe one.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const Icon = danger ? AlertTriangle : Info;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.12 }}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#18101C]/40 backdrop-blur-[2px] px-4"
      onClick={() => {
        if (!busy) onCancel?.();
      }}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        aria-describedby={descId}
        initial={{ opacity: 0, scale: 0.97, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
        className="bg-white rounded-2xl shadow-[0_24px_60px_-12px_rgba(24,16,28,0.28)] w-full max-w-[460px] font-outfit border border-black/[0.08] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-5">
          <div className="flex items-start gap-3.5">
            <span
              aria-hidden="true"
              className={`shrink-0 grid place-items-center h-10 w-10 rounded-xl ${
                danger
                  ? "bg-[#FEF2F2] text-[#B91C1C]"
                  : "bg-[#C95EBE]/[0.12] text-[#9C3D93]"
              }`}
            >
              <Icon size={19} strokeWidth={2.1} />
            </span>
            <div className="min-w-0 pt-1">
              <h2
                id={labelId}
                className="text-[18px] leading-snug font-semibold text-[#18181B] tracking-[-0.01em]"
              >
                {title}
              </h2>
            </div>
          </div>

          <p
            id={descId}
            className="text-[15px] text-[#52525B] mt-3.5 leading-[1.6]"
          >
            {message}
          </p>

          {tip && (
            <p className="text-[13.5px] text-[#71717A] mt-4 leading-[1.6] bg-[#FAFAFA] border border-black/[0.06] border-l-[3px] border-l-[#C95EBE]/40 rounded-lg px-3.5 py-3">
              {tip}
            </p>
          )}
        </div>

        {/* Its own band, so the irreversible button never reads as part of the
            sentence explaining it. */}
        <div className="px-6 py-4 flex items-center justify-end gap-2.5 bg-[#FAFAFA] border-t border-black/[0.07]">
          {secondaryAction &&
            (secondaryAction.href ? (
              <a
                href={secondaryAction.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mr-auto text-[13.5px] font-medium text-[#71717A] hover:text-[#18181C] underline underline-offset-[3px] whitespace-nowrap rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C95EBE]/50"
              >
                {secondaryAction.label}
              </a>
            ) : (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                disabled={busy}
                className="mr-auto text-[13.5px] font-medium text-[#71717A] hover:text-[#18181C] underline underline-offset-[3px] whitespace-nowrap disabled:opacity-60 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C95EBE]/50"
              >
                {secondaryAction.label}
              </button>
            ))}
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg text-[14.5px] font-medium text-[#3F3F46] bg-white border border-black/[0.12] hover:bg-[#F4F4F5] hover:border-black/[0.18] whitespace-nowrap disabled:opacity-60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C95EBE]/50 focus-visible:ring-offset-1"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-[14.5px] font-semibold text-white whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
              danger
                ? "bg-[#B91C1C] hover:bg-[#991B1B] focus-visible:ring-[#B91C1C]/50"
                : "bg-[#C95EBE] hover:bg-[#b04da6] focus-visible:ring-[#C95EBE]/50"
            }`}
          >
            {busy ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Working...
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ConfirmDialog;
